/**
 * OrganisasjonDialog - Presentational Component
 * Dialog for å opprette eller redigere organisasjon
 * Følger REACT_PATTERNS.md: Presentational komponent (kun UI rendering)
 */

import React from 'react';
import { Building2, User, Phone, Lock, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { phoneCountryCodes } from '@/lib/phoneCountryCodes';
import type { Organisasjon, CreateOrganisasjonData, UpdateOrganisasjonData } from '@/services/api';

interface OrganisasjonDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateOrganisasjonData | UpdateOrganisasjonData) => void;
  organisasjon?: Organisasjon | null; // null = opprett ny, undefined = ikke åpnet ennå
  loading?: boolean;
  error?: string | null;
}

const OrganisasjonDialog: React.FC<OrganisasjonDialogProps> = ({
  open,
  onClose,
  onSubmit,
  organisasjon = null,
  loading = false,
  error = null,
}) => {
  const isEdit = organisasjon !== null;
  const isNew = !isEdit;

  // Form state
  const [navn, setNavn] = React.useState('');
  const [slug, setSlug] = React.useState('');
  const [beskrivelse, setBeskrivelse] = React.useState('');
  const [kontaktpersonNavn, setKontaktpersonNavn] = React.useState('');
  const [kontaktpersonEmail, setKontaktpersonEmail] = React.useState('');
  const [kontaktpersonTelefon, setKontaktpersonTelefon] = React.useState('');
  const [kontaktpersonPhoneCountryCode, setKontaktpersonPhoneCountryCode] = React.useState('+47');
  const [isActive, setIsActive] = React.useState(true);
  const [allowGoogleOAuth, setAllowGoogleOAuth] = React.useState(true);
  const [allowFacebookOAuth, setAllowFacebookOAuth] = React.useState(true);
  
  // Admin-bruker (kun ved opprettelse)
  const [adminFirstName, setAdminFirstName] = React.useState('');
  const [adminLastName, setAdminLastName] = React.useState('');
  const [adminEmail, setAdminEmail] = React.useState('');
  const [adminPassword, setAdminPassword] = React.useState('');
  const [adminPhoneNumber, setAdminPhoneNumber] = React.useState('');
  const [adminPhoneCountryCode, setAdminPhoneCountryCode] = React.useState('+47');

  // Reset form når dialog åpnes/stenges
  React.useEffect(() => {
    if (open) {
      if (isEdit && organisasjon) {
        setNavn(organisasjon.navn || '');
        setSlug(organisasjon.slug || '');
        setBeskrivelse(organisasjon.beskrivelse || '');
        setKontaktpersonNavn(organisasjon.kontaktpersonNavn || '');
        setKontaktpersonEmail(organisasjon.kontaktpersonEmail || '');
        // Parse telefonnummer: hvis det starter med +, splitt i landkode og nummer
        const telefon = organisasjon.kontaktpersonTelefon || '';
        if (telefon.startsWith('+')) {
          const match = telefon.match(/^(\+\d{1,4})(.*)$/);
          if (match) {
            setKontaktpersonPhoneCountryCode(match[1]);
            setKontaktpersonTelefon(match[2]);
          } else {
            setKontaktpersonPhoneCountryCode('+47');
            setKontaktpersonTelefon(telefon);
          }
        } else {
          setKontaktpersonPhoneCountryCode('+47');
          setKontaktpersonTelefon(telefon);
        }
        setIsActive(organisasjon.isActive);
        setAllowGoogleOAuth(organisasjon.allowGoogleOAuth ?? true);
        setAllowFacebookOAuth(organisasjon.allowFacebookOAuth ?? true);
      } else {
        // Reset for ny organisasjon
        setNavn('');
        setSlug('');
        setBeskrivelse('');
        setKontaktpersonNavn('');
        setKontaktpersonEmail('');
        setKontaktpersonTelefon('');
        setKontaktpersonPhoneCountryCode('+47');
        setIsActive(true);
        setAllowGoogleOAuth(true);
        setAllowFacebookOAuth(true);
        setAdminFirstName('');
        setAdminLastName('');
        setAdminEmail('');
        setAdminPassword('');
        setAdminPhoneNumber('');
        setAdminPhoneCountryCode('+47');
      }
    }
  }, [open, isEdit, organisasjon]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Kombiner landkode + telefonnummer for kontaktperson
    const kontaktpersonTelefonFull = kontaktpersonTelefon
      ? `${kontaktpersonPhoneCountryCode}${kontaktpersonTelefon}`
      : undefined;

    if (isNew) {
      onSubmit({
        navn,
        slug: slug || undefined,
        beskrivelse: beskrivelse || undefined,
        kontaktpersonNavn: kontaktpersonNavn || undefined,
        kontaktpersonEmail: kontaktpersonEmail || undefined,
        kontaktpersonTelefon: kontaktpersonTelefonFull,
        isActive,
        allowGoogleOAuth,
        allowFacebookOAuth,
        adminFirstName,
        adminLastName,
        adminEmail,
        adminPassword,
        adminPhoneNumber,
        adminPhoneCountryCode,
      } as CreateOrganisasjonData);
    } else {
      onSubmit({
        navn,
        slug: slug || undefined,
        beskrivelse: beskrivelse || undefined,
        kontaktpersonNavn: kontaktpersonNavn || undefined,
        kontaktpersonEmail: kontaktpersonEmail || undefined,
        kontaktpersonTelefon: kontaktpersonTelefonFull,
        isActive,
        allowGoogleOAuth,
        allowFacebookOAuth,
      } as UpdateOrganisasjonData);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {isNew ? 'Opprett ny organisasjon' : 'Rediger organisasjon'}
          </DialogTitle>
          <DialogDescription>
            {isNew
              ? 'Opprett en ny organisasjon og første admin-bruker'
              : 'Oppdater organisasjonsinformasjon'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Aktiv-status */}
          <div
            className={`flex items-center justify-between gap-4 rounded-lg border p-4 ${
              isActive ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
            }`}
          >
            <div className="flex items-center gap-3">
              {isActive ? (
                <CheckCircle2 className="h-6 w-6 text-green-700" aria-hidden="true" />
              ) : (
                <XCircle className="h-6 w-6 text-red-700" aria-hidden="true" />
              )}
              <div>
                <p className={`font-semibold ${isActive ? 'text-green-800' : 'text-red-800'}`}>
                  {isActive ? 'Aktiv' : 'Inaktiv'}
                </p>
                <p className={`text-sm ${isActive ? 'text-green-700' : 'text-red-700'}`}>
                  {isActive ? 'Brukere kan logge inn' : 'Brukere kan ikke logge inn'}
                </p>
              </div>
            </div>
            <Switch
              id="isActive"
              aria-label="Organisasjon er aktiv"
              checked={isActive}
              onCheckedChange={setIsActive}
              className="data-[state=checked]:bg-green-600"
            />
          </div>

          {/* Organisasjonsinformasjon */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Organisasjonsinformasjon
            </h3>

            <div className="space-y-2">
              <Label htmlFor="navn">
                Navn <span className="text-destructive">*</span>
              </Label>
              <Input
                id="navn"
                value={navn}
                onChange={(e) => setNavn(e.target.value)}
                placeholder="F.eks. Oslo Teater"
                required
                className="bg-slate-50/50 focus:bg-white"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">Slug (valgfritt)</Label>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="oslo-teater"
                className="bg-slate-50/50 focus:bg-white"
              />
              <p className="text-xs text-muted-foreground">
                Kort identifikator for organisasjonen (brukes for branding)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="beskrivelse">Beskrivelse</Label>
              <Textarea
                id="beskrivelse"
                value={beskrivelse}
                onChange={(e) => setBeskrivelse(e.target.value)}
                placeholder="Beskrivelse av organisasjonen"
                rows={3}
                className="bg-slate-50/50 focus:bg-white"
              />
            </div>

            <Separator />

            <h4 className="text-base font-medium flex items-center gap-2">
              <User className="h-4 w-4" />
              Kontaktperson
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="kontaktpersonNavn">Navn</Label>
                <Input
                  id="kontaktpersonNavn"
                  value={kontaktpersonNavn}
                  onChange={(e) => setKontaktpersonNavn(e.target.value)}
                  placeholder="Fornavn Etternavn"
                  className="bg-slate-50/50 focus:bg-white"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="kontaktpersonEmail">E-post</Label>
                <Input
                  id="kontaktpersonEmail"
                  type="email"
                  value={kontaktpersonEmail}
                  onChange={(e) => setKontaktpersonEmail(e.target.value)}
                  placeholder="kontakt@example.com"
                  className="bg-slate-50/50 focus:bg-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="kontaktpersonTelefon" className="flex items-center gap-1">
                <Phone className="h-3.5 w-3.5" />
                Telefonnummer
              </Label>
              <div className="flex gap-2">
                <Select
                  value={kontaktpersonPhoneCountryCode}
                  onValueChange={(value) => setKontaktpersonPhoneCountryCode(value)}
                >
                  <SelectTrigger id="kontaktpersonPhoneCountryCode" className="w-28">
                    <SelectValue placeholder="+47" />
                  </SelectTrigger>
                  <SelectContent>
                    {phoneCountryCodes.map((country) => (
                      <SelectItem key={country.code} value={country.code}>
                        {country.flag} {country.code} {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  id="kontaktpersonTelefon"
                  value={kontaktpersonTelefon}
                  onChange={(e) => setKontaktpersonTelefon(e.target.value)}
                  placeholder="12345678"
                  className="flex-1 bg-slate-50/50 focus:bg-white"
                />
              </div>
            </div>

            <Separator />

            <h4 className="text-base font-medium">OAuth Innloggingsmetoder</h4>
            <p className="text-sm text-muted-foreground">
              Kontroller hvilke OAuth-leverandører brukere kan koble til kontoen sin
            </p>

            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="allowGoogleOAuth"
                  checked={allowGoogleOAuth}
                  onCheckedChange={(checked) => setAllowGoogleOAuth(checked === true)}
                />
                <Label htmlFor="allowGoogleOAuth" className="text-sm font-normal cursor-pointer">
                  Tillat Google-innlogging
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="allowFacebookOAuth"
                  checked={allowFacebookOAuth}
                  onCheckedChange={(checked) => setAllowFacebookOAuth(checked === true)}
                />
                <Label htmlFor="allowFacebookOAuth" className="text-sm font-normal cursor-pointer">
                  Tillat Facebook-innlogging
                </Label>
              </div>
            </div>

            {(!allowGoogleOAuth || !allowFacebookOAuth) && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Hvis du deaktiverer en OAuth-metode, vil brukere ikke kunne koble eller bruke denne metoden for innlogging
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Admin-bruker (kun ved opprettelse) */}
          {isNew && (
            <>
              <Separator />
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  Første admin-bruker
                </h3>
                <p className="text-sm text-muted-foreground">
                  Opprett første admin-bruker for denne organisasjonen
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="adminFirstName">
                      Fornavn <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="adminFirstName"
                      value={adminFirstName}
                      onChange={(e) => setAdminFirstName(e.target.value)}
                      placeholder="Ola"
                      required
                      className="bg-slate-50/50 focus:bg-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="adminLastName">
                      Etternavn <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="adminLastName"
                      value={adminLastName}
                      onChange={(e) => setAdminLastName(e.target.value)}
                      placeholder="Nordmann"
                      required
                      className="bg-slate-50/50 focus:bg-white"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminEmail">
                    E-post <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="adminEmail"
                    type="email"
                    value={adminEmail}
                    onChange={(e) => setAdminEmail(e.target.value)}
                    placeholder="admin@example.com"
                    required
                    className="bg-slate-50/50 focus:bg-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminPhoneNumber" className="flex items-center gap-1">
                    <Phone className="h-3.5 w-3.5" />
                    Telefonnummer <span className="text-destructive">*</span>
                  </Label>
                  <div className="flex gap-2">
                    <Select
                      value={adminPhoneCountryCode}
                      onValueChange={(value) => setAdminPhoneCountryCode(value)}
                    >
                      <SelectTrigger id="adminPhoneCountryCode" className="w-28">
                        <SelectValue placeholder="+47" />
                      </SelectTrigger>
                      <SelectContent>
                        {phoneCountryCodes.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.flag} {country.code} {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      id="adminPhoneNumber"
                      value={adminPhoneNumber}
                      onChange={(e) => setAdminPhoneNumber(e.target.value)}
                      placeholder="12345678"
                      required
                      className="flex-1 bg-slate-50/50 focus:bg-white"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminPassword">
                    Passord <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="adminPassword"
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    placeholder="Minimum 8 tegn"
                    required
                    minLength={8}
                    className="bg-slate-50/50 focus:bg-white"
                  />
                  <p className="text-xs text-muted-foreground">
                    Admin-brukeren kan endre passord etter første innlogging
                  </p>
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Avbryt
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Lagrer...' : isNew ? 'Opprett organisasjon' : 'Lagre endringer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OrganisasjonDialog;

